import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Calculator, DollarSign, Home, TrendingUp,
  RefreshCw, Repeat, CheckCircle, ArrowRight, Sparkles, FileText
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import { FREE_REPORT_GATE_MESSAGE } from "@/utils/planMessaging";
import { useReportQuota } from "@/hooks/useReportQuota";

const BRRRRCalculator = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const {
    reportsUsedThisMonth,
    isStarter,
    isFree,
    isProLike,
    starterLimitReached,
    reportPlanText,
  } = useReportQuota(Boolean(user && token));
  
  const [formData, setFormData] = useState({
    property_name: "",
    address: "",
    // Buy
    purchase_price: 150000,
    closing_costs_buy: 3000,
    // Rehab
    rehab_budget: 40000,
    rehab_duration_months: 3,
    holding_costs_monthly: 800,
    // Rent
    monthly_rent: 1800,
    vacancy_rate: 5,
    property_taxes_annual: 2400,
    insurance_annual: 1200,
    maintenance_percent: 5,
    capex_percent: 5,
    management_percent: 10,
    // Refinance
    after_repair_value: 240000,
    refinance_ltv: 75,
    refinance_interest_rate: 7,
    refinance_loan_term: 30,
    refinance_closing_costs: 3000,
    // Initial Financing
    initial_financing_type: "cash",
    initial_loan_amount: 0,
    initial_interest_rate: 12,
    initial_points: 2
  });

  const steps = [
    { name: "Buy", icon: Home, color: "text-cyan-500" },
    { name: "Rehab", icon: TrendingUp, color: "text-amber-500" },
    { name: "Rent", icon: DollarSign, color: "text-emerald-500" },
    { name: "Refinance", icon: RefreshCw, color: "text-purple-500" },
    { name: "Repeat", icon: Repeat, color: "text-pink-500" }
  ];

  useEffect(() => {
    if (id && token) {
      loadAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (!id && location?.state?.from_renovation) {
      const s = location.state;
      setFormData(prev => ({
        ...prev,
        purchase_price: s.purchase_price ?? prev.purchase_price,
        rehab_budget: s.rehab_estimate ?? prev.rehab_budget,
        after_repair_value: s.arv ?? prev.after_repair_value
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location && location.state]);

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analysis/brrrr/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      setFormData(response.data.inputs);
    } catch (error) {
      toast.error("Failed to load BRRRR analysis");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setAnalysis(null);
  };

  const calculateAnalysis = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/analysis/brrrr`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      toast.success("BRRRR Analysis calculated and saved!");
      navigate(`/brrrr-calculator/${response.data.id}`, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate analysis");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === "Infinite") return "∞";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    if (value === "Infinite") return "∞%";
    return `${(value || 0).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="brrrr-calculator-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">BRRRR Calculator</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              {analysis && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (isFree) {
                      toast.error(FREE_REPORT_GATE_MESSAGE);
                      return;
                    }
                    if (starterLimitReached) {
                      toast.error("Monthly report limit reached. Upgrade to Pro for unlimited reports.");
                      return;
                    }
                    navigate(`/report/brrrr/${analysis.id}`);
                  }}
                  disabled={starterLimitReached}
                  className="border-slate-700 text-slate-300 hover:text-white"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Report
                </Button>
              )}
              {analysis && (
                <div className="hidden md:flex flex-col leading-tight">
                  <span className="text-xs text-slate-300">{reportPlanText}</span>
                  {isStarter && (
                    <span className="text-xs text-slate-500">{reportsUsedThisMonth} of 5 reports used this month</span>
                  )}
                  {isProLike && (
                    <span className="text-xs text-slate-500">Unlimited report generation</span>
                  )}
                </div>
              )}
              {analysis && starterLimitReached && (
                <Button
                  size="sm"
                  onClick={() => navigate('/upgrade')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button 
                onClick={calculateAnalysis}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="calculate-brrrr-btn"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* BRRRR Steps Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <button
                onClick={() => setActiveStep(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeStep === index 
                    ? 'bg-slate-800 border border-slate-700' 
                    : 'hover:bg-slate-800/50'
                }`}
              >
                <step.icon className={`h-4 w-4 ${step.color}`} />
                <span className={`text-sm font-medium ${activeStep === index ? 'text-white' : 'text-slate-400'}`}>
                  {step.name}
                </span>
              </button>
              {index < steps.length - 1 && (
                <ArrowRight className="h-4 w-4 text-slate-600 mx-1" />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Property Info */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Property Info</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Property Name *</Label>
                  <Input
                    value={formData.property_name}
                    onChange={(e) => handleInputChange('property_name', e.target.value)}
                    placeholder="BRRRR Deal #1"
                    className="bg-slate-950 border-slate-800 text-white"
                    data-testid="brrrr-property-name-input"
                  />
                </div>
              </div>
            </div>

            {/* Step 1: Buy */}
            {activeStep === 0 && (
              <div className="glass-card rounded-xl p-6 border-l-4 border-cyan-500">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Home className="h-5 w-5 text-cyan-500" />
                  Buy Phase
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Purchase Price</Label>
                    <Input
                      type="number"
                      value={formData.purchase_price}
                      onChange={(e) => handleInputChange('purchase_price', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                      data-testid="brrrr-purchase-price"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Closing Costs</Label>
                    <Input
                      type="number"
                      value={formData.closing_costs_buy}
                      onChange={(e) => handleInputChange('closing_costs_buy', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Initial Financing</Label>
                    <Select 
                      value={formData.initial_financing_type} 
                      onValueChange={(v) => handleInputChange('initial_financing_type', v)}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="cash">All Cash</SelectItem>
                        <SelectItem value="hard_money">Hard Money Loan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.initial_financing_type === 'hard_money' && (
                    <>
                      <div>
                        <Label className="text-slate-300">Hard Money Loan Amount</Label>
                        <Input
                          type="number"
                          value={formData.initial_loan_amount}
                          onChange={(e) => handleInputChange('initial_loan_amount', parseFloat(e.target.value) || 0)}
                          className="bg-slate-950 border-slate-800 text-white font-mono"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300">Interest Rate (%)</Label>
                          <Input
                            type="number"
                            value={formData.initial_interest_rate}
                            onChange={(e) => handleInputChange('initial_interest_rate', parseFloat(e.target.value) || 0)}
                            className="bg-slate-950 border-slate-800 text-white font-mono"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Points (%)</Label>
                          <Input
                            type="number"
                            value={formData.initial_points}
                            onChange={(e) => handleInputChange('initial_points', parseFloat(e.target.value) || 0)}
                            className="bg-slate-950 border-slate-800 text-white font-mono"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Rehab */}
            {activeStep === 1 && (
              <div className="glass-card rounded-xl p-6 border-l-4 border-amber-500">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                  Rehab Phase
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Rehab Budget</Label>
                    <Input
                      type="number"
                      value={formData.rehab_budget}
                      onChange={(e) => handleInputChange('rehab_budget', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Rehab Duration (Months)</Label>
                    <Input
                      type="number"
                      value={formData.rehab_duration_months}
                      onChange={(e) => handleInputChange('rehab_duration_months', parseInt(e.target.value) || 1)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Holding Costs (Monthly)</Label>
                    <Input
                      type="number"
                      value={formData.holding_costs_monthly}
                      onChange={(e) => handleInputChange('holding_costs_monthly', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Rent */}
            {activeStep === 2 && (
              <div className="glass-card rounded-xl p-6 border-l-4 border-emerald-500">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                  Rent Phase
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Monthly Rent</Label>
                    <Input
                      type="number"
                      value={formData.monthly_rent}
                      onChange={(e) => handleInputChange('monthly_rent', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Vacancy Rate (%)</Label>
                      <Input
                        type="number"
                        value={formData.vacancy_rate}
                        onChange={(e) => handleInputChange('vacancy_rate', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Management (%)</Label>
                      <Input
                        type="number"
                        value={formData.management_percent}
                        onChange={(e) => handleInputChange('management_percent', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Property Taxes/Year</Label>
                      <Input
                        type="number"
                        value={formData.property_taxes_annual}
                        onChange={(e) => handleInputChange('property_taxes_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Insurance/Year</Label>
                      <Input
                        type="number"
                        value={formData.insurance_annual}
                        onChange={(e) => handleInputChange('insurance_annual', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Refinance */}
            {activeStep === 3 && (
              <div className="glass-card rounded-xl p-6 border-l-4 border-purple-500">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-purple-500" />
                  Refinance Phase
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">After Repair Value (ARV)</Label>
                    <Input
                      type="number"
                      value={formData.after_repair_value}
                      onChange={(e) => handleInputChange('after_repair_value', parseFloat(e.target.value) || 0)}
                      className="bg-slate-950 border-slate-800 text-white font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Refinance LTV (%)</Label>
                      <Input
                        type="number"
                        value={formData.refinance_ltv}
                        onChange={(e) => handleInputChange('refinance_ltv', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Interest Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.125"
                        value={formData.refinance_interest_rate}
                        onChange={(e) => handleInputChange('refinance_interest_rate', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Loan Term (Years)</Label>
                      <Select 
                        value={formData.refinance_loan_term.toString()} 
                        onValueChange={(v) => handleInputChange('refinance_loan_term', parseInt(v))}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                          <SelectItem value="10">10 Years</SelectItem>
                          <SelectItem value="15">15 Years</SelectItem>
                          <SelectItem value="20">20 Years</SelectItem>
                          <SelectItem value="25">25 Years</SelectItem>
                          <SelectItem value="30">30 Years</SelectItem>
                          <SelectItem value="35">35 Years</SelectItem>
                          <SelectItem value="40">40 Years</SelectItem>
                          <SelectItem value="50">50 Years</SelectItem>
                          <SelectItem value="75">75 Years</SelectItem>
                          <SelectItem value="100">100 Years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Refinance Costs</Label>
                      <Input
                        type="number"
                        value={formData.refinance_closing_costs}
                        onChange={(e) => handleInputChange('refinance_closing_costs', parseFloat(e.target.value) || 0)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Repeat - Summary */}
            {activeStep === 4 && (
              <div className="glass-card rounded-xl p-6 border-l-4 border-pink-500">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Repeat className="h-5 w-5 text-pink-500" />
                  Repeat - Ready to Analyze!
                </h3>
                <div className="space-y-4">
                  <p className="text-slate-400">
                    Review your inputs and click Calculate to see your BRRRR analysis results.
                  </p>
                  <div className="bg-slate-950/50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Purchase Price</span>
                      <span className="font-mono text-white">{formatCurrency(formData.purchase_price)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Rehab Budget</span>
                      <span className="font-mono text-white">{formatCurrency(formData.rehab_budget)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Monthly Rent</span>
                      <span className="font-mono text-white">{formatCurrency(formData.monthly_rent)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">ARV</span>
                      <span className="font-mono text-white">{formatCurrency(formData.after_repair_value)}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={calculateAnalysis}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Calculate BRRRR Analysis
                  </Button>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className="border-slate-700 text-slate-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              <Button
                onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                disabled={activeStep === 4}
                className="bg-slate-800 hover:bg-slate-700 text-white"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!analysis ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <Repeat className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">BRRRR Strategy Analysis</h3>
                <p className="text-slate-400 mb-6">Complete all phases and calculate to see your results</p>
                <div className="text-left text-sm text-slate-500 space-y-1">
                  <p><span className="text-cyan-500 font-bold">B</span>uy - Purchase below market value</p>
                  <p><span className="text-amber-500 font-bold">R</span>ehab - Force appreciation through renovation</p>
                  <p><span className="text-emerald-500 font-bold">R</span>ent - Generate income from tenants</p>
                  <p><span className="text-purple-500 font-bold">R</span>efinance - Pull out your capital</p>
                  <p><span className="text-pink-500 font-bold">R</span>epeat - Reinvest in next property</p>
                </div>
              </div>
            ) : (
              <>
                {/* Infinite Return Badge */}
                {analysis.results.infinite_return && (
                  <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/50 rounded-xl p-6 text-center">
                    <Sparkles className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                    <h3 className="text-2xl font-bold text-emerald-500">Infinite Return!</h3>
                    <p className="text-slate-300 mt-2">You've pulled out all your cash and still have positive cash flow</p>
                  </div>
                )}

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card" data-testid="brrrr-cash-left">
                    <div className="text-sm text-slate-400 mb-1">Cash Left in Deal</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.cash_left_in_deal <= 0 ? 'text-emerald-500' : 'text-white'}`}>
                      {formatCurrency(analysis.results.cash_left_in_deal)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Monthly Cash Flow</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(analysis.results.monthly_cash_flow)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Cash-on-Cash Return</div>
                    <div className="text-2xl font-bold font-mono text-amber-500">
                      {analysis.results.cash_on_cash_return === "Infinite" ? "∞%" : formatPercent(analysis.results.cash_on_cash_return)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Equity Captured</div>
                    <div className="text-2xl font-bold font-mono text-cyan-500">
                      {formatCurrency(analysis.results.equity_captured)}
                    </div>
                  </div>
                </div>

                {/* Phase Breakdown */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">BRRRR Breakdown</h3>
                  
                  {/* Buy Phase */}
                  <div className="mb-4 pb-4 border-b border-slate-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Home className="h-4 w-4 text-cyan-500" />
                      <span className="font-semibold text-white">Buy Phase</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Purchase + Closing</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.purchase_price + analysis.results.closing_costs_buy)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Rehab Phase */}
                  <div className="mb-4 pb-4 border-b border-slate-800">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-amber-500" />
                      <span className="font-semibold text-white">Rehab Phase</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Rehab Budget</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.rehab_budget)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Holding Costs</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.holding_costs)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Cash In */}
                  <div className="mb-4 pb-4 border-b border-slate-800 bg-slate-950/30 p-3 rounded-lg">
                    <div className="flex justify-between font-semibold">
                      <span className="text-slate-300">Total Cash In Deal</span>
                      <span className="font-mono text-amber-500">{formatCurrency(analysis.results.cash_in_deal)}</span>
                    </div>
                  </div>

                  {/* Refinance Phase */}
                  <div className="mb-4 pb-4 border-b border-slate-800">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-4 w-4 text-purple-500" />
                      <span className="font-semibold text-white">Refinance Phase</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">ARV</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.after_repair_value)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">New Loan ({formData.refinance_ltv}% LTV)</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.new_loan_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Cash Out</span>
                        <span className="font-mono text-emerald-500">{formatCurrency(analysis.results.cash_out)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Rent Phase */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-emerald-500" />
                      <span className="font-semibold text-white">Rent Phase</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Annual NOI</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.noi)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Annual Debt Service</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.annual_debt_service)}</span>
                      </div>
                      <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-slate-700">
                        <span className="text-slate-300">Annual Cash Flow</span>
                        <span className={`font-mono ${analysis.results.annual_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {formatCurrency(analysis.results.annual_cash_flow)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Investment Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-950/50 rounded-lg p-3">
                      <div className="text-xs text-slate-400 mb-1">Cap Rate</div>
                      <div className="font-mono text-white">{formatPercent(analysis.results.cap_rate)}</div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-3">
                      <div className="text-xs text-slate-400 mb-1">DSCR</div>
                      <div className={`font-mono ${analysis.results.dscr >= 1.25 ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {analysis.results.dscr.toFixed(2)}x
                      </div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-3">
                      <div className="text-xs text-slate-400 mb-1">Monthly Mortgage</div>
                      <div className="font-mono text-white">{formatCurrency(analysis.results.monthly_mortgage)}</div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-3">
                      <div className="text-xs text-slate-400 mb-1">Monthly Rent</div>
                      <div className="font-mono text-white">{formatCurrency(analysis.results.monthly_rent)}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BRRRRCalculator;
